import { useState, useEffect, useRef, useCallback } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { API_URL } from '@/config';
import storage from '@/utils/storage';

export const useNotificationStream = (enabled: boolean) => {
  const [notification, setNotification] = useState<any>({});
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const previousAccessTokenRef = useRef<string | null>(null);

  const teamData = storage?.getTeam();
  const xTeamIdFromLocalStorage = teamData ? teamData._id : null;
  const xTeamId = xTeamIdFromLocalStorage;

  const establishSSEConnection = useCallback(
    (accessToken: string, teamId: string) => {
      if (!enabled || !teamId || !accessToken) return;

      if (eventSourceRef.current) eventSourceRef.current.close();

      const url = `${API_URL}/v1/notifications/stream`;
      const newEventSource = new EventSourcePolyfill(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Team': teamId,
        },
        withCredentials: false,
      });
      eventSourceRef.current = newEventSource;

      newEventSource.onopen = () => {
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
      };

      newEventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setNotification(data);
      };

      newEventSource.onerror = () => {
        newEventSource.close();
        reconnectTimerRef.current = setTimeout(() => {
          const currentToken = storage.getAccessToken();
          const currentTeamData = storage.getTeam();
          const currentTeamId = currentTeamData ? currentTeamData._id : null;
          if (currentToken && currentTeamId) {
            establishSSEConnection(currentToken, currentTeamId);
          }
        }, 5000);
      };
    },
    [enabled]
  );

  useEffect(() => {
    const access_token = storage.getAccessToken();

    // Check if access token has changed
    if (
      previousAccessTokenRef.current !== null &&
      previousAccessTokenRef.current !== access_token
    ) {
      // Access token changed, close current connection and establish new one
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      // Establish new connection with updated token if enabled
      if (enabled && access_token && xTeamId) {
        establishSSEConnection(access_token, xTeamId);
      }
    } else if (enabled && !eventSourceRef.current && access_token && xTeamId) {
      // Initial connection or reconnection needed
      establishSSEConnection(access_token, xTeamId);
    } else if (!enabled) {
      // Disabled, close any existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }

    // Update the previous access token reference
    previousAccessTokenRef.current = access_token;

    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [enabled, xTeamId, establishSSEConnection]);

  const terminateSSEConnection = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
  };

  return { notification, establishSSEConnection, terminateSSEConnection };
};
